import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, Chip, CircularProgress, Divider, Alert,
} from '@mui/material';
import { Send as SendIcon, AttachFile as AttachIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import useAtsSenderName from '../../hooks/useAtsSenderName';

/**
 * Modal de Oferta. Dos secciones:
 *  - Enviar oferta: plantilla del proyecto (preview) + carta adjunta (opcional) + envío, con registro.
 *  - Resultado de la oferta: aceptada / rechazada (+ motivo).
 * Se abre desde el botón "Oferta" de la tarjeta.
 */
const AtsOfferDialog = ({ open, onClose, application, positionName = '', orgName = '', onOfferChange }) => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  // Resultado (aceptar/rechazar)
  const [offerState, setOfferState] = useState({ offerStatus: null, offerRejectionReason: null });
  const [offerReason, setOfferReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Envío de la oferta (email + carta)
  const [template, setTemplate] = useState({ subject: '', body: '' });
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [sending, setSending] = useState(false);
  const [letterFile, setLetterFile] = useState(null);
  const [sentAtLocal, setSentAtLocal] = useState(null);
  const fileRef = useRef(null);
  const { senderName, setSenderName } = useAtsSenderName(open);

  useEffect(() => {
    if (!open) return;
    setOfferState({
      offerStatus: application?.offerStatus || null,
      offerRejectionReason: application?.offerRejectionReason || null,
    });
    setRejecting(false);
    setOfferReason('');
    setLetterFile(null);
    setSentAtLocal(application?.offerEmailSentAt || null);

    const loadTemplate = async () => {
      try {
        setLoadingTpl(true);
        const res = await api.get(`/projects/ats-offer-email`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) setTemplate({ subject: data.subject || '', body: data.body || '' });
      } catch (err) {
        console.error(err);
        showAlert(t('ats.offer.loadError'), 'error');
      } finally {
        setLoadingTpl(false);
      }
    };
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, application]);

  const candidato = application?.candidateName || application?.candidateEmail || '';

  const resolve = (text) => String(text || '')
    .replace(/\{\{\s*candidato\s*\}\}/g, candidato)
    .replace(/\{\{\s*cargo\s*\}\}/g, positionName || '')
    .replace(/\{\{\s*empresa\s*\}\}/g, orgName || '');

  const resolvedSubject = resolve(template.subject);
  const resolvedBody = resolve(template.body);

  const formatSentAt = (ts) => {
    if (!ts) return '';
    try {
      if (typeof ts === 'object' && ts._seconds) return new Date(ts._seconds * 1000).toLocaleString('es-CL');
      const d = new Date(ts);
      return isNaN(d.getTime()) ? '' : d.toLocaleString('es-CL');
    } catch (e) {
      return '';
    }
  };

  const handleSendOffer = async () => {
    if (!application) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('emailSubject', resolvedSubject);
      fd.append('emailBody', resolvedBody);
      fd.append('senderName', senderName);
      if (letterFile) fd.append('letter', letterFile);
      const res = await api.post(
        `/applications/${application.uid || application.id}/offer-email`,
        fd,
        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.response?.status_response === 'OK') {
        const sentAt = res.data.response?.data?.offerEmailSentAt || new Date().toISOString();
        setSentAtLocal(sentAt);
        setLetterFile(null);
        if (onOfferChange) onOfferChange(application, { offerEmailSentAt: sentAt });
        showAlert(t('ats.offer.sent'), 'success');
      } else {
        showAlert(t('ats.offer.sendError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.offer.sendError'), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleSetOffer = async (offerStatus, rejectionReason = null) => {
    if (!application) return;
    setSaving(true);
    try {
      await api.patch(
        `/applications/${application.uid || application.id}/offer`,
        { offerStatus, rejectionReason },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const updated = {
        offerStatus: offerStatus === 'pending' ? null : offerStatus,
        offerRejectionReason: offerStatus === 'rejected' ? (rejectionReason || null) : null,
      };
      setOfferState(updated);
      setRejecting(false);
      setOfferReason('');
      if (onOfferChange) onOfferChange(application, updated);
      showAlert(t('ats.offer.saved'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.offer.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.offer.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {candidato}
        </Typography>

        {/* ── Enviar oferta ── */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t('ats.offer.sendTitle')}
        </Typography>

        {sentAtLocal && (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            {t('ats.offer.lastSent')} {formatSentAt(sentAtLocal)}
            {application.offerEmail?.sentBy ? ` — ${application.offerEmail.sentBy}` : ''}
          </Alert>
        )}

        {loadingTpl ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            <TextField fullWidth size="small" sx={{ mb: 1.5 }}
              label={t('ats.config.emailSender.fieldLabel')} value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              inputProps={{ maxLength: 80 }} />
            <TextField fullWidth size="small" sx={{ mb: 1.5 }}
              label={t('ats.offer.subject')} value={resolvedSubject}
              InputProps={{ readOnly: true }} />
            <TextField fullWidth multiline minRows={6} sx={{ mb: 1.5 }}
              label={t('ats.offer.body')} value={resolvedBody}
              InputProps={{ readOnly: true }} />
          </>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Button size="small" variant="outlined" startIcon={<AttachIcon />}
            onClick={() => fileRef.current && fileRef.current.click()}>
            {t('ats.offer.attachLetter')}
          </Button>
          {letterFile && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {letterFile.name}
              </Typography>
              <Button size="small" color="inherit" onClick={() => setLetterFile(null)}>
                {t('ats.offer.removeLetter')}
              </Button>
            </>
          )}
          {!letterFile && application.offerEmail?.letterUrl && (
            <Button size="small" color="inherit"
              onClick={() => window.open(application.offerEmail.letterUrl, '_blank')}>
              {t('ats.offer.viewLastLetter')}
            </Button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,application/pdf" hidden
            onChange={(e) => { setLetterFile(e.target.files?.[0] || null); e.target.value = ''; }} />
        </Box>

        <Button onClick={handleSendOffer} variant="contained" disabled={sending || loadingTpl}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}>
          {sentAtLocal ? t('ats.offer.resendOffer') : t('ats.offer.sendButton')}
        </Button>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Resultado de la oferta ── */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t('ats.offer.title')}
        </Typography>

        {offerState.offerStatus === 'accepted' && (
          <Chip label={t('ats.offer.accepted')} size="small" color="success" sx={{ fontWeight: 600 }} />
        )}
        {offerState.offerStatus === 'rejected' && (
          <Box>
            <Chip label={t('ats.offer.rejected')} size="small" color="error" sx={{ fontWeight: 600 }} />
            {offerState.offerRejectionReason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('ats.offer.reason')}: {offerState.offerRejectionReason}
              </Typography>
            )}
          </Box>
        )}
        {!offerState.offerStatus && (
          <Typography variant="caption" color="text.disabled">{t('ats.offer.pending')}</Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          {!rejecting ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" color="success"
                variant={offerState.offerStatus === 'accepted' ? 'contained' : 'outlined'}
                disabled={saving}
                onClick={() => handleSetOffer('accepted')}>
                {t('ats.offer.markAccepted')}
              </Button>
              <Button size="small" color="error"
                variant={offerState.offerStatus === 'rejected' ? 'contained' : 'outlined'}
                disabled={saving}
                onClick={() => { setRejecting(true); setOfferReason(offerState.offerRejectionReason || ''); }}>
                {t('ats.offer.markRejected')}
              </Button>
              {offerState.offerStatus && (
                <Button size="small" color="inherit" disabled={saving}
                  onClick={() => handleSetOffer('pending')}>
                  {t('ats.offer.clear')}
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              <TextField fullWidth multiline minRows={2} size="small"
                label={t('ats.offer.reason')}
                placeholder={t('ats.offer.reasonPlaceholder')}
                value={offerReason}
                onChange={(e) => setOfferReason(e.target.value)}
                sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="inherit" disabled={saving}
                  onClick={() => { setRejecting(false); setOfferReason(''); }}>
                  {t('cancel')}
                </Button>
                <Button size="small" variant="contained" color="error"
                  disabled={saving || !offerReason.trim()}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={() => handleSetOffer('rejected', offerReason.trim())}>
                  {t('ats.offer.confirmReject')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsOfferDialog;
